import {Form, Select, Table, Tag, Tooltip} from 'antd';
import styles from './assetValuation.module.scss';
import {GET_ALL_ORDERS} from 'graphql/queries/stocks';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useState} from 'react';
import {stockOrderStatus} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {instrumentKeyLabel} from 'pages/AssetManagement/modules/instrumentKeys';
import {formatEnumType} from 'utils/helper';
import UserLink from 'components/userLink/UserLink';

export const InstrumentOrders = ({instrumentId, name}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [copied, setCopied] = useState(false);
  const [form] = Form.useForm();
  const status = Form.useWatch('status', form);

  const {loading, refetch, data} = useQuery(GET_ALL_ORDERS, {
    variables: {
      input: {
        instrumentId,
        paginate: true,
        newFirst: true,
        limit: 10,
        page: 1,
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      input: {
        page,
        limit: pageSize,
        paginate: true,
        status: status ?? null,
        instrumentId,
        openOrders: ['OPEN'].includes(status) ? true : null,
        newFirst: true,
      },
    });
  };

  const handleCopy = referenceId => {
    navigator.clipboard.writeText(referenceId).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 400);
    });
  };

  const handleFilterOrders = values => {
    setCurrentPage(1);
    setLimit(10);
    refetch({
      input: {
        instrumentId,
        paginate: true,
        newFirst: true,
        limit: 10,
        page: 1,
        status: values?.status ?? null,
        openOrders: ['OPEN'].includes(values?.status) ? true : null,
      },
    });
  };

  const columns = [
    {
      title: 'Order ID',
      key: 'id',
      dataIndex: 'id',
      render: (text, record) => (
        <Tooltip trigger="hover" title={copied ? 'Copied' : 'Copy'}>
          <b
            style={{cursor: 'pointer'}}
            onClick={e => {
              e.stopPropagation();
              handleCopy(record?.id);
            }}>
            {record?.id}
          </b>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      key: 'user',
      dataIndex: 'user',
      render: (text, record) => (
        <div className={styles['record-label']}>
          {record?.user?.imageUrl ? (
            <img src={record?.user?.imageUrl} alt="" />
          ) : (
            <div className={styles['dummyAvatar']}>
              {record?.user?.firstName?.charAt(0)}
              {record?.user?.lastName?.charAt(0)}
            </div>
          )}
          <h4>
            <UserLink userId={record?.user?.id} tab="transactions?tab=stock">
              {record?.user?.firstName} {record?.user?.lastName}
            </UserLink>
          </h4>
        </div>
      ),
    },
    {
      title: 'Ticker',
      key: 'symbol',
      dataIndex: 'symbol',
      render: (text, render) => <span>{render?.symbol}</span>,
    },
    {
      title: 'Direction',
      key: 'direction',
      dataIndex: 'direction',
    },
    {
      title: 'Instrument',
      key: 'instrumentKey',
      dataIndex: 'instrumentKey',
      render: (text, render) => (
        <Tag
          color={
            {
              'BOND': 'cyan',
              'STOCK': 'red',
              'MUTUAL_FUND': 'purple',
            }[render?.instrumentKey]
          }>
          {instrumentKeyLabel(render?.instrumentKey)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (text, render) => (
        <Tag
          color={
            {
              'FILLED': 'green',
              'PENDING': 'orange',
              'ERROR': 'red',
              'REJECTED': 'red',
              'CANCELLED': 'red',
              'SETTLED': 'cyan',
              'PARTIALLY_FILLED': 'yellow',
            }[render?.status] || 'blue'
          }
          key={render.id}>
          {formatEnumType(render?.status)}
        </Tag>
      ),
    },
    {
      title: 'Currency',
      key: 'assetCurrency',
      dataIndex: 'assetCurrency',
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      render: (text, render) => (
        <Tag color={render?.type === 'LIMIT' ? 'black' : 'default'}>
          {formatEnumType(render?.type)}
        </Tag>
      ),
    },
    {
      title: 'Hold Status',
      key: 'hold',
      dataIndex: 'hold',
      render: (text, render) =>
        render?.hold ? (
          <Tag color={render?.hold ? 'red' : ''}>
            {render?.hold ? 'On hold' : ''}
          </Tag>
        ) : (
          '- -'
        ),
    },

    {
      title: 'Price',
      key: 'price',
      dataIndex: 'price',
      render: (text, render) => <span>{render?.price?.toLocaleString()}</span>,
    },
    {
      title: 'Static Fee',
      key: 'staticFee',
      dataIndex: 'staticFee',
      render: (text, record) => <span>{record?.staticFee?.toFixed(2)}</span>,
    },
    {
      title: 'Floating Fee',
      key: 'FloatingFee',
      dataIndex: 'floatingFee',
      render: (text, record) => <span>{record?.floatingFee?.toFixed(2)}</span>,
    },

    {
      title: 'Recurring Fee',
      key: 'recurringFee',
      dataIndex: 'recurringFee',
      render: (text, record) => <span>{record?.recurringFee?.toFixed(2)}</span>,
    },
    {
      title: 'Commission Fee',
      key: 'commissionFee',
      dataIndex: 'commissionFee',
      render: (text, record) => (
        <span>{record?.commissionFee?.toFixed(2)}</span>
      ),
    },
    {
      title: 'Exit Fee',
      key: 'exitFee',
      dataIndex: 'exitFee',
      render: (text, record) => <span>{record?.exitFee?.toFixed(2)}</span>,
    },
    {
      title: 'Entry Fee',
      key: 'entryFee',
      dataIndex: 'entryFee',
      render: (text, record) => <span>{record?.entryFee?.toFixed(2)}</span>,
    },
    {
      title: 'Fee Charged',
      key: 'feeCharged',
      dataIndex: 'feeCharged',
      render: (text, render) => <span>{render?.feeCharged?.toFixed(2)}</span>,
    },
    {
      title: 'Quantity',
      key: 'quantity',
      dataIndex: 'quantity',
    },
  ];

  return (
    <div className={styles.ordersTable}>
      <div className={styles.tHeader}>
        <h2>{name} Orders</h2>
        <Form
          form={form}
          layout="horizontal"
          className={styles.form}
          onFinish={handleFilterOrders}>
          <Form.Item name="status">
            <Select
              showSearch
              allowClear
              style={{width: 200}}
              placeholder="Filter by status"
              options={stockOrderStatus?.map(data => ({
                label: data?.name,
                value: data?.key,
              }))}
            />
          </Form.Item>
          <SubmitButton smallWidth label="Filter" />
        </Form>
      </div>
      <Table
        loading={loading}
        className={styles.table}
        dataSource={data?.getOrders?.data}
        columns={columns}
        pagination={{
          pageSize: limit,
          current: currentPage,
          total: data?.getOrders?.pagination?.total,
          onChange: handlePageChange,
        }}
      />
    </div>
  );
};
